//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnifiedMetricsLiftStatsResponse } from '@bvengineering/metrics-gateway-service-types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoMap } from 'pojo-maps';

import * as metricsApi from '@apis/metrics/metricsApi';
import {
  EcommerceSessionType,
  FanreelDisplayType,
  IStatsRequest,
  InsightsSourceType,
  SortDirection,
  SortKey,
  UmsBenchmarksResponse,
} from '@apis/metrics/metricsApiModel';
import {
  CONTENT_COVERAGE_DEFAULT_LIMIT,
  CONTENT_COVERAGE_DEFAULT_PAGE_NUMBER,
  ContentCoverageTableView,
  initialBaseStatsRequest,
} from '@insights/insightsConstants';
import {
  GALLERY_DISPLAY_TYPES,
  isGalleryDisplayType,
} from '@insights/insightsSidebarConstants';
import {
  IInsightsContentCoverageStats,
  IInsightsGalleriesPageState,
  IInsightsGalleriesPagesState,
  ReelIdType,
} from '@insights/redux/galleries/insightsGalleriesModel';
import { updateExistingGallery } from '@insights/redux/galleries/insightsGalleriesUtils';
import {
  IInsightsActivityStats,
  IInsightsBubbleStats,
  IInsightsExperienceStats,
  IInsightsGraphStats,
} from '@insights/redux/insightsPageModel';
import {
  fetchApiActivityStats,
  getTestVariationFromParams,
} from '@insights/redux/insightsPageUtils';
import { getMetadata } from '@insights/redux/metadata/insightsMetadataSlice';
import { ILabel } from '@models/ILabel';
import Interval from '@models/Interval';
import ReelId from '@models/ReelId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import IntervalHelper from '@utils/intervalHelper';

const initialState: IInsightsGalleriesPagesState = {
  summary: {
    liftStats: undefined,
    benchmarkStats: undefined,
    experienceStats: undefined,
    statsRequestBase: undefined,
    bubbleStats: undefined,
    graphStats: undefined,
    activityStats: initialBaseStatsRequest,
    allLabels: undefined,
    flags: {
      isFetchingBubbleStats: false,
      isFetchingGraphStats: false,
      isFetchingActivityStats: false,
      isFetchingLiftStats: false,
      isFetchingBenchmarkStats: false,
      isFetchingExperienceStats: false,
      isFetchingLabels: false,
    },
    errors: {
      fetchBubbleStatsError: undefined,
      fetchGraphStatsError: undefined,
      fetchActivityStatsError: undefined,
      fetchLiftStatsError: undefined,
      fetchBenchmarkStatsError: undefined,
      fetchExperienceStatsError: undefined,
    },
  },
  galleries: PojoMap.empty<ReelId, IInsightsGalleriesPageState>(),
};

const galleriesPageInitialState: IInsightsGalleriesPageState = {
  bubbleStats: undefined,
  graphStats: undefined,
  activityStats: initialBaseStatsRequest,
  allLabels: undefined,
  contentCoverageStats: {
    view: ContentCoverageTableView.GRID,
    limit: CONTENT_COVERAGE_DEFAULT_LIMIT,
    offset: CONTENT_COVERAGE_DEFAULT_PAGE_NUMBER,
    avgContentCoverage: 0,
    graphStats: undefined,
    productStats: undefined,
  },
  benchmarkStats: undefined,
  flags: {
    isFetchingBubbleStats: false,
    isFetchingGraphStats: false,
    isFetchingActivityStats: false,
    isFetchingContentCoverageStats: false,
    isFetchingBenchmarkStats: false,
    isFetchingLabels: false,
  },
  errors: {
    fetchBubbleStatsError: undefined,
    fetchGraphStatsError: undefined,
    fetchActivityStatsError: undefined,
    fetchContentCoverageStatsError: undefined,
    fetchBenchmarkStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InsightsGalleries,
  initialState,
  reducers: {
    requestSummaryBubbleStats(state) {
      state.summary.flags.isFetchingBubbleStats = true;
      state.summary.errors.fetchBubbleStatsError = undefined;
    },
    recieveSummaryBubbleStatsSucceeded(
      state,
      action: PayloadAction<IInsightsBubbleStats>,
    ) {
      state.summary.flags.isFetchingBubbleStats = false;
      state.summary.bubbleStats = action.payload;
    },
    recieveSummaryBubbleStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingBubbleStats = false;
      state.summary.errors.fetchBubbleStatsError = action.payload;
    },
    requestSummaryGraphStats(state) {
      state.summary.flags.isFetchingGraphStats = true;
      state.summary.errors.fetchGraphStatsError = undefined;
    },
    recieveSummaryGraphStatsSucceeded(
      state,
      action: PayloadAction<IInsightsGraphStats>,
    ) {
      state.summary.flags.isFetchingGraphStats = false;
      state.summary.graphStats = action.payload;
    },
    recieveSummaryGraphStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingGraphStats = false;
      state.summary.errors.fetchGraphStatsError = action.payload;
    },
    requestSummaryActivityStats(state) {
      state.summary.flags.isFetchingActivityStats = true;
      state.summary.errors.fetchActivityStatsError = undefined;
    },
    recieveSummaryActivityStatsSucceeded(
      state,
      action: PayloadAction<IInsightsActivityStats>,
    ) {
      state.summary.flags.isFetchingActivityStats = false;
      state.summary.activityStats = action.payload;
    },
    recieveSummaryActivityStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingActivityStats = false;
      state.summary.errors.fetchActivityStatsError = action.payload;
    },
    updateSummaryActivityStats(
      state,
      action: PayloadAction<Partial<IInsightsActivityStats>>,
    ) {
      state.summary.activityStats = {
        ...state.summary.activityStats,
        ...action.payload,
      };
    },
    requestSummaryLiftStats(state) {
      state.summary.flags.isFetchingLiftStats = true;
      state.summary.errors.fetchLiftStatsError = undefined;
    },
    recieveSummaryLiftStatsSucceeded(
      state,
      action: PayloadAction<UnifiedMetricsLiftStatsResponse>,
    ) {
      state.summary.flags.isFetchingLiftStats = false;
      state.summary.liftStats = action.payload;
    },
    recieveSummaryLiftStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingLiftStats = false;
      state.summary.errors.fetchLiftStatsError = action.payload;
    },
    requestSummaryBenchmarkStats(state) {
      state.summary.flags.isFetchingBenchmarkStats = true;
      state.summary.errors.fetchBenchmarkStatsError = undefined;
    },
    recieveSummaryBenchmarkStatsSucceeded(
      state,
      action: PayloadAction<UmsBenchmarksResponse>,
    ) {
      state.summary.flags.isFetchingBenchmarkStats = false;
      state.summary.benchmarkStats = action.payload;
    },
    recieveSummaryBenchmarkStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingBenchmarkStats = false;
      state.summary.errors.fetchBenchmarkStatsError = action.payload;
    },
    requestSummaryExperienceStats(state) {
      state.summary.flags.isFetchingExperienceStats = true;
      state.summary.errors.fetchExperienceStatsError = undefined;
    },
    recieveSummaryExperienceStatsSucceeded(
      state,
      action: PayloadAction<IInsightsExperienceStats>,
    ) {
      state.summary.flags.isFetchingExperienceStats = false;
      state.summary.experienceStats = action.payload;
    },
    recieveSummaryExperienceStatsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingExperienceStats = false;
      state.summary.errors.fetchExperienceStatsError = action.payload;
    },
    requestGalleryBubbleStats(state, action: PayloadAction<ReelIdType>) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingBubbleStats = true;
        gallery.errors.fetchBubbleStatsError = undefined;
      });
    },
    recieveGalleryBubbleStatsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: IInsightsBubbleStats;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingBubbleStats = false;
        gallery.bubbleStats = action.payload.stats;
      });
    },
    recieveGalleryBubbleStatsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingBubbleStats = false;
        gallery.errors.fetchBubbleStatsError = action.payload.error;
      });
    },
    requestGalleryGraphStats(state, action: PayloadAction<ReelIdType>) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingGraphStats = true;
        gallery.errors.fetchGraphStatsError = undefined;
      });
    },
    recieveGalleryGraphStatsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: IInsightsGraphStats;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingGraphStats = false;
        gallery.graphStats = action.payload.stats;
      });
    },
    recieveGalleryGraphStatsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingGraphStats = false;
        gallery.errors.fetchGraphStatsError = action.payload.error;
      });
    },
    requestGalleryActivityStats(state, action: PayloadAction<ReelIdType>) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingActivityStats = true;
        gallery.errors.fetchActivityStatsError = undefined;
      });
    },
    recieveGalleryActivityStatsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: IInsightsActivityStats;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingActivityStats = false;
        gallery.activityStats = action.payload.stats;
      });
    },
    recieveGalleryActivityStatsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingActivityStats = false;
        gallery.errors.fetchActivityStatsError = action.payload.error;
      });
    },
    updateGalleryActivityStats(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: Partial<IInsightsActivityStats>;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.activityStats = {
          ...gallery.activityStats,
          ...action.payload.stats,
        };
      });
    },
    requestGalleryContentCoverageStats(
      state,
      action: PayloadAction<ReelIdType>,
    ) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingContentCoverageStats = true;
        gallery.errors.fetchContentCoverageStatsError = undefined;
      });
    },
    recieveGalleryContentCoverageStatsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: IInsightsContentCoverageStats;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingContentCoverageStats = false;
        gallery.contentCoverageStats = action.payload.stats;
      });
    },
    recieveGalleryContentCoverageStatsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingContentCoverageStats = false;
        gallery.errors.fetchContentCoverageStatsError = action.payload.error;
      });
    },
    requestGalleryBenchmarkStats(state, action: PayloadAction<ReelIdType>) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingBenchmarkStats = true;
        gallery.errors.fetchBenchmarkStatsError = undefined;
      });
    },
    recieveGalleryBenchmarkStatsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        stats: UmsBenchmarksResponse;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingBenchmarkStats = false;
        gallery.benchmarkStats = action.payload.stats;
      });
    },
    recieveGalleryBenchmarkStatsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingBenchmarkStats = false;
        gallery.errors.fetchBenchmarkStatsError = action.payload.error;
      });
    },
    addGalleryPage(state, action: PayloadAction<ReelIdType>) {
      const galleries = PojoMap.set(
        state.galleries,
        action.payload,
        galleriesPageInitialState,
      );
      state.galleries = galleries;
    },
    setSummaryStatsRequestBase(state, action: PayloadAction<IStatsRequest>) {
      state.summary.statsRequestBase = action.payload;
    },
    setGalleryStatsRequestBase(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        statsRequest: IStatsRequest;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.statsRequestBase = action.payload.statsRequest;
      });
    },
    updateContentCoverageSortParams(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        view?: ContentCoverageTableView;
        limit?: number;
        offset?: number;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.contentCoverageStats.view =
          action.payload.view ?? gallery.contentCoverageStats.view;
        gallery.contentCoverageStats.limit =
          action.payload.limit ?? gallery.contentCoverageStats.limit;
        gallery.contentCoverageStats.offset =
          action.payload.offset ?? gallery.contentCoverageStats.offset;
      });
    },
    requestSummaryLabels(state) {
      state.summary.flags.isFetchingLabels = true;
      state.summary.errors.fetchLabelsError = undefined;
    },
    recieveSummaryLabelsSucceeded(state, action: PayloadAction<ILabel[]>) {
      state.summary.flags.isFetchingLabels = false;
      state.summary.allLabels = action.payload;
    },
    recieveSummaryLabelsFailed(state, action: PayloadAction<string>) {
      state.summary.flags.isFetchingLabels = false;
      state.summary.errors.fetchLabelsError = action.payload;
    },
    requestGalleryLabels(state, action: PayloadAction<ReelIdType>) {
      updateExistingGallery(action.payload, state, (gallery) => {
        gallery.flags.isFetchingLabels = true;
        gallery.errors.fetchLabelsError = undefined;
      });
    },
    recieveGalleryLabelsSucceeded(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        labels: ILabel[];
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingLabels = false;
        gallery.allLabels = action.payload.labels;
      });
    },
    recieveGalleryLabelsFailed(
      state,
      action: PayloadAction<{
        gallery: ReelIdType;
        error: string;
      }>,
    ) {
      updateExistingGallery(action.payload.gallery, state, (gallery) => {
        gallery.flags.isFetchingLabels = false;
        gallery.errors.fetchLabelsError = action.payload.error;
      });
    },
    resetState(state) {
      state = initialState;
    },
  },
});

export const fetchSummaryBubbleStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    const { dateRange } = getState().insights.metadata;
    if (statsRequestBase) {
      let statsRequest;
      if (statsRequestBase.comparisonInterval) {
        statsRequest = statsRequestBase;
      } else {
        const comparisonInterval = Interval.fromDateRange(
          IntervalHelper.getComparisonDateRange(dateRange),
        );
        statsRequest = {
          ...statsRequestBase,
          comparisonInterval,
        };
      }
      dispatch(actions.requestSummaryBubbleStats());
      try {
        const bubbleStatsRes = await metricsApi.getBubbleStats(statsRequest);
        const emailStatsRes =
          await metricsApi.getEmailSignupCount(statsRequestBase);
        const isEmailSignupsEnabled =
          await metricsApi.getEmailSignupEnabled(statsRequestBase);
        const bubbleStats: IInsightsBubbleStats = {
          stats: bubbleStatsRes[0],
          emailSignups: emailStatsRes,
          isEmailSignupsEnabled: isEmailSignupsEnabled,
        };
        dispatch(actions.recieveSummaryBubbleStatsSucceeded(bubbleStats));
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryBubbleStatsFailed(err));
      }
    }
  },
);

export const fetchSummaryGraphStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryGraphStats());
      try {
        const graphStatsRes = await metricsApi.getGraphStats(statsRequestBase);
        const graphStats: IInsightsGraphStats = {
          stats: graphStatsRes,
        };
        dispatch(actions.recieveSummaryGraphStatsSucceeded(graphStats));
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryGraphStatsFailed(err));
      }
    }
  },
);

export const fetchSummaryActivityStats = createThunk(
  () => async (dispatch, getState) => {
    const { activityStats, statsRequestBase } =
      getState().insights.galleries.summary;
    const { contentType, limit, offset, sortDir, sortKey } = activityStats;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryActivityStats());
      try {
        const itemProductStatsRequest = {
          ...statsRequestBase,
          sortKey,
          sortDirection: sortDir,
          limit,
          offset,
          containerId: undefined,
          excludeContainerId: undefined,
        };
        const activityStatsRes: IInsightsActivityStats =
          await fetchApiActivityStats(
            contentType,
            itemProductStatsRequest,
            activityStats,
          );
        dispatch(
          actions.recieveSummaryActivityStatsSucceeded(activityStatsRes),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryActivityStatsFailed(err));
      }
    }
  },
);

export const fetchSummaryLiftStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryLiftStats());
      try {
        const liftStatsRes = await metricsApi.getLiftStats(statsRequestBase);
        dispatch(actions.recieveSummaryLiftStatsSucceeded(liftStatsRes));
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryLiftStatsFailed(err));
      }
    }
  },
);

export const fetchPerformanceLiftStat = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryLiftStats());
      try {
        const liftStatsRes = await metricsApi.getLiftStats({
          ...statsRequestBase,
          sourceType: [InsightsSourceType.REEL],
        });
        dispatch(actions.recieveSummaryLiftStatsSucceeded(liftStatsRes));
      } catch (e) {
        dispatch(
          actions.recieveSummaryLiftStatsFailed(
            getRequestErrorMessage(e) ?? '',
          ),
        );
      }
    }
  },
);

export const fetchSummaryBenchmarkStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryBenchmarkStats());
      try {
        const benchmarkStatsRes =
          await metricsApi.getBenchmarkStats(statsRequestBase);
        dispatch(
          actions.recieveSummaryBenchmarkStatsSucceeded(benchmarkStatsRes),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryBenchmarkStatsFailed(err));
      }
    }
  },
);

export const fetchSummaryExperienceStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryExperienceStats());
      try {
        const touchpointStatsRes =
          await metricsApi.getTouchpointStats(statsRequestBase);
        dispatch(
          actions.recieveSummaryExperienceStatsSucceeded({
            stats: touchpointStatsRes,
          }),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryExperienceStatsFailed(err));
      }
    }
  },
);

export const fetchGalleryBubbleStats = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const { dateRange } = getState().insights.metadata;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { statsRequestBase } = selectedGallery;
      if (statsRequestBase) {
        let statsRequest;
        if (statsRequestBase.comparisonInterval) {
          statsRequest = statsRequestBase;
        } else {
          const comparisonInterval = Interval.fromDateRange(
            IntervalHelper.getComparisonDateRange(dateRange),
          );
          statsRequest = {
            ...statsRequestBase,
            comparisonInterval,
          };
        }
        dispatch(actions.requestGalleryBubbleStats(gallery));
        try {
          const bubbleStatsRes = await metricsApi.getBubbleStats(statsRequest);
          const bubbleStats: IInsightsBubbleStats = {
            stats: bubbleStatsRes[0],
          };
          dispatch(
            actions.recieveGalleryBubbleStatsSucceeded({
              gallery,
              stats: bubbleStats,
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(
            actions.recieveGalleryBubbleStatsFailed({ gallery, error: err }),
          );
        }
      }
    }
  },
);

export const fetchGalleryGraphStats = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { statsRequestBase } = selectedGallery;
      if (statsRequestBase) {
        dispatch(actions.requestGalleryGraphStats(gallery));
        try {
          const graphStatsRes =
            await metricsApi.getGraphStats(statsRequestBase);
          const graphStats: IInsightsGraphStats = {
            stats: graphStatsRes,
          };
          dispatch(
            actions.recieveGalleryGraphStatsSucceeded({
              gallery,
              stats: graphStats,
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(
            actions.recieveGalleryGraphStatsFailed({ gallery, error: err }),
          );
        }
      }
    }
  },
);

export const fetchGalleryActivityStats = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { activityStats, statsRequestBase } = selectedGallery;
      const { contentType, limit, offset, sortDir, sortKey } = activityStats;
      if (statsRequestBase) {
        dispatch(actions.requestGalleryActivityStats(gallery));
        try {
          const itemProductStatsRequest = {
            ...statsRequestBase,
            sortKey,
            sortDirection: sortDir,
            limit,
            offset,
            containerId: undefined,
            excludeContainerId: undefined,
          };
          const activityStatsRes: IInsightsActivityStats =
            await fetchApiActivityStats(
              contentType,
              itemProductStatsRequest,
              activityStats,
            );
          dispatch(
            actions.recieveGalleryActivityStatsSucceeded({
              gallery,
              stats: activityStatsRes,
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(
            actions.recieveGalleryActivityStatsFailed({ gallery, error: err }),
          );
        }
      }
    }
  },
);

export const fetchGalleryContentCoverageStats = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { contentCoverageStats, statsRequestBase } = selectedGallery;
      const { limit, offset } = contentCoverageStats;
      if (statsRequestBase) {
        dispatch(actions.requestGalleryContentCoverageStats(gallery));
        const itemProductStatsRequest = {
          ...statsRequestBase,
          sortKey: SortKey.PAGE_IMPRESSIONS_NO_CONTENT,
          sortDirection: SortDirection.DESC,
          limit,
          offset,
          containerId: undefined,
          excludeContainerId: undefined,
        };
        try {
          const graphStatsRes =
            await metricsApi.getGraphStats(statsRequestBase);
          const graphStats: IInsightsGraphStats = {
            stats: graphStatsRes,
          };
          const itemStatsRes = await metricsApi.getTaggedProductStats(
            itemProductStatsRequest,
          );
          const bubbleStatsRes =
            await metricsApi.getBubbleStats(statsRequestBase);
          const contentCoverageStatsRes: IInsightsContentCoverageStats = {
            ...contentCoverageStats,
            avgContentCoverage: bubbleStatsRes[0]?.pageImpressionCoverage || 0,
            graphStats: graphStats,
            productStats: itemStatsRes,
          };
          dispatch(
            actions.recieveGalleryContentCoverageStatsSucceeded({
              gallery,
              stats: contentCoverageStatsRes,
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(
            actions.recieveGalleryContentCoverageStatsFailed({
              gallery,
              error: err,
            }),
          );
        }
      }
    }
  },
);

export const fetchGalleryBenchmarkStats = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { statsRequestBase } = selectedGallery;
      if (statsRequestBase) {
        dispatch(actions.requestGalleryBenchmarkStats(gallery));
        try {
          const benchmarkStatsRes =
            await metricsApi.getBenchmarkStats(statsRequestBase);
          dispatch(
            actions.recieveGalleryBenchmarkStatsSucceeded({
              gallery,
              stats: benchmarkStatsRes,
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(
            actions.recieveGalleryBenchmarkStatsFailed({ gallery, error: err }),
          );
        }
      }
    }
  },
);

export const fetchSummaryLabels = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getState().insights.galleries.summary;
    if (statsRequestBase) {
      dispatch(actions.requestSummaryLabels());
      try {
        const labelsResponse = await metricsApi.getLabels(statsRequestBase);
        dispatch(
          actions.recieveSummaryLabelsSucceeded(
            labelsResponse.map((label) => {
              return {
                label: label.label,
                count: label.count,
                value: label.label,
              };
            }),
          ),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.recieveSummaryLabelsFailed(err));
      }
    }
  },
);

export const initSummaryStatsRequestBase = createThunk(
  (ecommerceSessionType: EcommerceSessionType) =>
    async (dispatch, getState) => {
      if (!getState().insights.metadata.flags.hasFetchedMetadata) {
        await dispatch(getMetadata());
      }
      const { createdOn, dateRange } = getState().insights.metadata;
      dispatch(
        getSummaryStats({
          interval: IntervalHelper.fromDateRange(dateRange, createdOn),
          ecommerceSessionType: ecommerceSessionType,
          sourceType: [InsightsSourceType.REEL],
          testVariation: getTestVariationFromParams(),
        }),
      );
    },
);

export const fetchGalleryLabels = createThunk(
  (gallery: ReelIdType) => async (dispatch, getState) => {
    const { galleries } = getState().insights.galleries;
    const selectedGallery = PojoMap.get(galleries, gallery);
    if (selectedGallery) {
      const { statsRequestBase } = selectedGallery;
      if (statsRequestBase) {
        dispatch(actions.requestGalleryLabels(gallery));
        try {
          const labelsResponse = await metricsApi.getLabels(statsRequestBase);
          dispatch(
            actions.recieveGalleryLabelsSucceeded({
              gallery: gallery,
              labels: labelsResponse.map((label) => {
                return {
                  label: label.label,
                  count: label.count,
                  value: label.label,
                };
              }),
            }),
          );
        } catch (e) {
          const err = getRequestErrorMessage(e) ?? '';
          dispatch(actions.recieveGalleryLabelsFailed({ gallery, error: err }));
        }
      }
    }
  },
);

export const initGalleryStatsRequestBase = createThunk(
  (
    ecommerceSessionType: EcommerceSessionType,
    gallery: ReelIdType,
    galleryDisplayType?: ReelIdType,
  ) =>
    async (dispatch, getState) => {
      if (!getState().insights.metadata.flags.hasFetchedMetadata) {
        await dispatch(getMetadata());
      }
      const { createdOn, dateRange } = getState().insights.metadata;
      let statsRequest;

      if (isGalleryDisplayType(gallery)) {
        statsRequest = {
          interval: IntervalHelper.fromDateRange(dateRange, createdOn),
          ecommerceSessionType: ecommerceSessionType,
          sourceType: [InsightsSourceType.REEL],
          displayType: gallery as FanreelDisplayType,
        };
      } else {
        statsRequest = {
          interval: IntervalHelper.fromDateRange(dateRange, createdOn),
          ecommerceSessionType: ecommerceSessionType,
          sourceType: [InsightsSourceType.REEL],
          sourceId: gallery,
        };
      }
      dispatch(
        getGalleryStats(gallery, galleryDisplayType, {
          ...statsRequest,
          testVariation: getTestVariationFromParams(),
        }),
      );
    },
);

export const getSummaryStats = createThunk(
  (statsRequestBase: IStatsRequest) => (dispatch) => {
    dispatch(actions.setSummaryStatsRequestBase(statsRequestBase));
    void dispatch(fetchSummaryBubbleStats());
    void dispatch(fetchSummaryGraphStats());
    void dispatch(fetchSummaryExperienceStats());
    void dispatch(fetchSummaryActivityStats());
    void dispatch(fetchSummaryBenchmarkStats());
    void dispatch(fetchSummaryLiftStats());
  },
);

export const getGalleryStats = createThunk(
  (
    pageSource: ReelIdType,
    galleryDisplayType: ReelIdType | undefined,
    statsRequestBase: IStatsRequest,
  ) =>
    (dispatch) => {
      dispatch(
        actions.setGalleryStatsRequestBase({
          gallery: pageSource,
          statsRequest: statsRequestBase,
        }),
      );

      if (isDefined(pageSource)) {
        void dispatch(fetchGalleryBubbleStats(pageSource));
        void dispatch(fetchGalleryGraphStats(pageSource));
        void dispatch(fetchGalleryActivityStats(pageSource));
        void dispatch(fetchGalleryBenchmarkStats(pageSource));

        if (galleryDisplayType === GALLERY_DISPLAY_TYPES.PRODUCT_DETAIL) {
          void dispatch(fetchGalleryContentCoverageStats(pageSource));
        }
      }
    },
);

export const {
  addGalleryPage,
  resetState,
  updateContentCoverageSortParams,
  updateGalleryActivityStats,
  updateSummaryActivityStats,
} = actions;

export default reducer;
